import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { type ForceChangePasswordSchema, passwordSchema } from '@/api/auth/auth.schema';
import { Button } from '@/components/ui/button';
import { FormInput } from '@/components/ui/form-fields/form-input';
import { Icon } from '@/components/ui/icon';
import { ContentLoader } from '@/components/ui/loaders';
import { CONFIG } from '@/config-globals';
import { useRouter } from '@/hooks/use-router';
import { animations } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useForceChangePassword, useIsAuthenticated } from '@/services/auth';

const changePasswordFormSchema = z
  .object({
    newPassword: passwordSchema,
    confirmPassword: z.string('Campo obrigatório!').min(1, 'Campo obrigatório!'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'As senhas não conferem!',
    path: ['confirmPassword'],
  });

type ChangePasswordFormValues = z.infer<typeof changePasswordFormSchema>;

export const Route = createFileRoute('/change-password')({
  beforeLoad: ({ context: { auth } }) => {
    if (!auth.isAuthenticated) {
      return redirect({ to: '/sign-in' });
    }
  },
  component: RouteComponent,
});

/**
 * Tela de troca obrigatória de senha — só é alcançável enquanto `needToChangePassword`
 * estiver ligado (reset feito por um admin). `_app/route.tsx` já redireciona pra cá nesse
 * caso; aqui só falta cobrir quem tenta acessar direto sem precisar (manda pra `/`).
 */
function RouteComponent() {
  const [isLeaving, setIsLeaving] = useState(false);
  const router = useRouter();
  const { user, isLoading } = useIsAuthenticated();
  const { mutate: forceChangePassword, isPending } = useForceChangePassword();

  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordFormSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
    shouldUseNativeValidation: false,
  });

  useEffect(() => {
    if (!isLoading && user && !user.needToChangePassword) {
      router.replace('/');
    }
  }, [isLoading, user, router]);

  const handleSubmit = (data: ChangePasswordFormValues) => {
    const payload: ForceChangePasswordSchema = { newPassword: data.newPassword };

    forceChangePassword(payload, {
      onSuccess: () => {
        setIsLeaving(true);
        setTimeout(() => router.push('/'), 400);
      },
    });
  };

  if (isLoading || !user?.needToChangePassword) return <ContentLoader show={isLoading} />;

  return (
    <div
      className={cn(
        'flex items-center justify-center h-screen px-4 md:px-0',
        'relative z-0',
        'bg-[url("../public/bg.jpg")] md:bg-cover md:bg-center',
      )}
    >
      <div className="absolute w-full h-screen top-0 left-0 z-10 bg-black/60 dark:bg-black/80" />

      <motion.form
        initial={{ opacity: 0, y: 40 }}
        animate={
          isLeaving ? { opacity: 0, y: -40, scale: 0.98 } : { opacity: 1, y: 0, scale: 1 }
        }
        transition={{ duration: 0.4 }}
        variants={animations.fadeUp}
        id="form"
        onSubmit={form.handleSubmit(handleSubmit)}
        className={cn(
          'bg-background/80 md:bg-background-60',
          'w-full max-w-lg px-4 py-8',
          'rounded-[20px]',
          'shadow-md shadow-black/50',
          'flex flex-col gap-6 justify-center items-center',
          'relative z-20',
        )}
      >
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="flex items-center justify-center size-14 rounded-full bg-primary/10 text-primary">
            <Icon name="key-round" size={28} />
          </div>
          <h1 className="text-2xl font-bold">Defina sua nova senha</h1>
          <p className="text-sm text-muted-foreground max-w-sm">
            Sua senha foi resetada por um administrador. Escolha uma nova senha pra
            continuar usando o {CONFIG.APP_NAME}.
          </p>
        </div>

        <div className="w-full flex flex-col gap-4">
          <FormInput
            name="newPassword"
            control={form.control}
            label="Nova senha"
            type="password"
            leftAddon={<Icon name="lock" />}
            placeholder="********"
            md={12}
            lg={12}
          />
          <FormInput
            name="confirmPassword"
            control={form.control}
            label="Confirmar nova senha"
            type="password"
            leftAddon={<Icon name="lock" />}
            placeholder="********"
            md={12}
            lg={12}
          />
        </div>

        <Button
          type="submit"
          isLoading={isPending || isLeaving}
          disabled={isPending}
          form="form"
          className="w-full flex items-center gap-2"
        >
          <Icon name="save" />
          Salvar
        </Button>
      </motion.form>
    </div>
  );
}
